import { Component, computed, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DatePicker } from 'primeng/datepicker';

import {
  formatDateInputMask,
  parseFormattedDate,
  resolveDateInputCursor,
} from '../../helpers/date-input-mask';

@Component({
  selector: 'app-mayorap-datepicker',
  standalone: true,
  imports: [FormsModule, DatePicker],
  templateUrl: './mayorap-datepicker.component.html',
  styleUrl: './mayorap-datepicker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MayorapDatepickerComponent),
      multi: true,
    },
  ],
})
export class MayorapDatepickerComponent implements ControlValueAccessor {
  readonly inputId = input<string>();
  readonly placeholder = input('DD/MM/AAAA');
  readonly dateFormat = input('dd/mm/yy');
  readonly invalid = input(false);
  readonly centered = input(true);
  readonly maxWidth = input('22rem');
  readonly maxToday = input(false);

  readonly maxDate = computed(() => (this.maxToday() ? MayorapDatepickerComponent.startOfToday() : undefined));

  readonly value = signal<Date | null>(null);
  readonly isDisabled = signal(false);

  private onChange: (value: Date | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(value: Date | null): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  onValueChange(value: Date | null): void {
    if (value && !this.isDateAllowed(value)) {
      this.value.set(null);
      this.onChange(null);
      this.onTouched();
      return;
    }

    this.value.set(value);
    this.onChange(value);
    this.onTouched();
  }

  onDateInput(event: Event): void {
    const input = event.target;

    if (!(input instanceof HTMLInputElement)) {
      return;
    }

    const previousValue = input.value;
    const cursorPosition = input.selectionStart ?? previousValue.length;
    const digitsBeforeCursor = previousValue.slice(0, cursorPosition).replace(/\D/g, '').length;
    const formatted = formatDateInputMask(previousValue);

    if (formatted !== previousValue) {
      input.value = formatted;
      const nextCursor = resolveDateInputCursor(formatted, digitsBeforeCursor);
      input.setSelectionRange(nextCursor, nextCursor);
    }

    this.syncModelFromInput(formatted);
  }

  onDateBlur(): void {
    this.onTouched();
  }

  private syncModelFromInput(formatted: string): void {
    if (!formatted) {
      if (this.value() !== null) {
        this.value.set(null);
        this.onChange(null);
      }
      return;
    }

    const parsed = parseFormattedDate(formatted);

    if (!parsed || !this.isDateAllowed(parsed)) {
      if (this.value() !== null) {
        this.value.set(null);
        this.onChange(null);
      }
      return;
    }

    const current = this.value();

    if (!current || current.getTime() !== parsed.getTime()) {
      this.value.set(parsed);
      this.onChange(parsed);
    }
  }

  private isDateAllowed(date: Date): boolean {
    if (!this.maxToday()) {
      return true;
    }

    return MayorapDatepickerComponent.startOfDay(date).getTime() <= MayorapDatepickerComponent.startOfToday().getTime();
  }

  private static startOfToday(): Date {
    return MayorapDatepickerComponent.startOfDay(new Date());
  }

  private static startOfDay(date: Date): Date {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  }
}
